/**
 * Creature registry: assetId (config/constants.js EVOLUTION_CHAIN) → cartoon drawer.
 * Every creature is drawn centred on the origin inside its disc of radius r — the
 * physics circle — so collisions keep matching what the player sees.
 */
import { INK } from "../palette.js";
import { TAU, inkText } from "../toon-kit.js";
import { drawDisc, discShine } from "./disc.js";
import { SEA_CREATURES } from "./sea-creatures.js";
import { GARDEN_CREATURES } from "./garden-creatures.js";
import { DINO_CREATURES } from "./dino-creatures.js";

/** @type {Record<string, { disc: { base: string, light: string, shade: string }, discPattern?: Function, draw: Function }>} */
export var CREATURES = {};

/** Register drawers (creature modules add themselves here). */
export function registerCreatures(map) {
  for (var k in map) {
    if (Object.prototype.hasOwnProperty.call(map, k)) CREATURES[k] = map[k];
  }
}

registerCreatures(SEA_CREATURES);
registerCreatures(GARDEN_CREATURES);
registerCreatures(DINO_CREATURES);

var FALLBACK = { base: "#9aa8b8", light: "#d6dee8", shade: "#6c7a8a" };

/**
 * Draw one creature on its disc, centred at the origin.
 * @param {CanvasRenderingContext2D} c
 * @param {string} assetId
 * @param {number} r disc radius
 * @param {{ mood?: string, t?: number, silhouette?: boolean, noDisc?: boolean, noCreature?: boolean, noShine?: boolean }} [pose]
 *   noDisc: creature only; noCreature: disc only (with shine unless noShine)
 */
export function drawCreature(c, assetId, r, pose) {
  var p = pose || {};
  var def = CREATURES[assetId];
  c.save();
  if (p.silhouette) {
    drawSilhouette(c, assetId, r, p);
    c.restore();
    return;
  }
  if (!p.noDisc) drawDisc(c, r, def ? def.disc : FALLBACK, { pattern: def && def.discPattern });
  if (!p.noCreature) {
    if (def) def.draw(c, r, p);
    else inkText(c, (assetId || "?").slice(0, 2).toUpperCase(), 0, 0, r * 0.7, "#ffffff");
  }
  if (!p.noDisc && !p.noShine) discShine(c, r);
  c.restore();
}

/** Only the disc's shine (glint), for a non-rotating overlay over a rolling creature. */
export function drawCreatureShine(c, r) {
  discShine(c, r);
}

/** Locked silhouette tones: a slate shape, cel-lit like everything else (shade lower right). */
var LOCKED_DISC = {
  base: "#9fb4c8", light: "#c8d6e4", shade: "#7c92a8", rim: "rgba(255,255,255,0.25)",
};
var LOCKED_BASE = "#3a5066";
var LOCKED_SHADE = INK;

/**
 * Locked look: the creature as a dark slate shape on a muted disc (evolution ring teaser).
 * Drawn by painting the creature to a scratch canvas and filling its alpha: the shade tone
 * first, then the base tone through the same shape shifted up-left, which leaves the family's
 * shade crescent on the lower right, so a locked chip still reads as a solid, lit toy.
 */
function drawSilhouette(c, assetId, r, p) {
  var def = CREATURES[assetId];
  drawDisc(c, r, LOCKED_DISC);
  if (!def || typeof document === "undefined") return;
  // Scratch canvases at the target's own pixel scale (the registry bakes at 2×), so the
  // silhouette edge is as crisp as the rest of the chip.
  var m = c.getTransform ? c.getTransform() : null;
  var k = m ? Math.max(1, Math.hypot(m.a, m.b)) : 1;
  var size = Math.ceil(r * 2.4 * k);
  var shape = flatShape(def, r, size, k, p, LOCKED_SHADE);
  var lit = flatShape(def, r, size, k, p, LOCKED_BASE);
  var sc = shape.getContext("2d", { willReadFrequently: true }) || shape.getContext("2d");
  sc.globalCompositeOperation = "source-atop";
  sc.drawImage(lit, -r * 0.05 * k, -r * 0.065 * k);
  c.save();
  c.beginPath();
  c.arc(0, 0, r * 0.97, 0, TAU);
  c.clip();
  c.globalAlpha = 0.9;
  c.drawImage(shape, -size / 2 / k, -size / 2 / k, size / k, size / k);
  c.restore();
  discShine(c, r);
}

/** The creature's alpha on a size×size scratch canvas (drawn at scale k), filled with `color`. */
function flatShape(def, r, size, k, p, color) {
  var scratch = document.createElement("canvas");
  scratch.width = scratch.height = size;
  // CPU-backed like the chip it is copied into (a GPU scratch would force a readback there).
  var sc = scratch.getContext("2d", { willReadFrequently: true }) || scratch.getContext("2d");
  sc.translate(size / 2, size / 2);
  sc.scale(k, k);
  def.draw(sc, r, { mood: p.mood, t: p.t });
  sc.setTransform(1, 0, 0, 1, 0, 0);
  sc.globalCompositeOperation = "source-in";
  sc.fillStyle = color;
  sc.fillRect(0, 0, size, size);
  sc.globalCompositeOperation = "source-over";
  return scratch;
}
